import { useOrganization } from '@clerk/clerk-react';
import { zodResolver } from '@hookform/resolvers/zod';
import { Loader2 } from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';

import ZeePersonaDataFormSchema from '@/components/forms/schemas/zee-candidate/ZeePersonaDataFormSchema';
import { ZeePersonaDataForm } from '@/components/forms/zee-candidate/ZeePersonaDataForm';
import { Button } from '@/components/ui/button';
import { toast } from '@/hooks/ui/use-toast';
import { useCreateZeePersonaData } from '@/hooks/zee-persona-data/useCreateZeePersonaData';
import { useGetZeePersonaData } from '@/hooks/zee-persona-data/useGetZeePersonaData';
import { useUpdateZeePersonaData } from '@/hooks/zee-persona-data/useUpdateZeePersonaData';

type ZeePersonaDataFormValues = z.infer<typeof ZeePersonaDataFormSchema>;

interface LeadPersonaTabProps {
  candidateId?: string;
}

const commaSeparatedStringToArray = (
  value: string | string[] | null
): string[] => {
  if (!value) return [];
  if (Array.isArray(value)) return value;
  return value
    .split(',')
    .map((item) => item.trim())
    .filter(Boolean);
};

const arrayToCommaSeparatedString = (value: string[] | undefined): string => {
  if (!value || value.length === 0) return '';
  return value.join(', ');
};

const LeadPersonaTab = ({ candidateId = '' }: LeadPersonaTabProps) => {
  const { organization } = useOrganization();

  const [dynamicBehaviors, setDynamicBehaviors] = useState<string[]>([]);
  const [dynamicInterests, setDynamicInterests] = useState<string[]>([]);
  const [dynamicPersonality, setDynamicPersonality] = useState<string[]>([]);

  const form = useForm<ZeePersonaDataFormValues>({
    resolver: zodResolver(ZeePersonaDataFormSchema),
    defaultValues: {
      age: undefined,
      gender: undefined,
      marital_status: undefined,
      education: undefined,
      rentorhomeowner: undefined,
      behaviors: [],
      interests: [],
      personality: [],
      children_between_ages_0_3: undefined,
      children_age_ranges: [],
    },
  });

  const {
    result: personaDataResult,
    isLoading: isPersonaDataLoading,
    isSuccess: isPersonaDataSuccess,
  } = useGetZeePersonaData(candidateId, organization?.id || '');

  const {
    mutate: updatePersonaData,
    isError: isUpdatePersonaDataError,
    isSuccess: isUpdatePersonaDataSuccess,
    isLoading: isUpdatePersonaDataLoading,
  } = useUpdateZeePersonaData();

  const {
    mutate: createPersonaData,
    isError: isCreatePersonaDataError,
    isSuccess: isCreatePersonaDataSuccess,
    isLoading: isCreatePersonaDataLoading,
  } = useCreateZeePersonaData();

  const resetForm = useCallback(() => {
    if (personaDataResult?.data?.persona_data) {
      const data = personaDataResult.data.persona_data;

      const behaviorsArray = commaSeparatedStringToArray(
        data.behaviors as string | string[] | null
      );
      const interestsArray = commaSeparatedStringToArray(
        data.interests as string | string[] | null
      );
      const personalityArray = commaSeparatedStringToArray(
        data.personality as string | string[] | null
      );

      setDynamicBehaviors(behaviorsArray);
      setDynamicInterests(interestsArray);
      setDynamicPersonality(personalityArray);

      form.reset({
        age: data.age || undefined,
        gender: data.gender || undefined,
        marital_status: data.marital_status || undefined,
        education: data.education || undefined,
        rentorhomeowner: data.rentorhomeowner || undefined,
        behaviors: behaviorsArray,
        interests: interestsArray,
        personality: personalityArray,
        children_between_ages_0_3: data.children_between_ages_0_3 || undefined,
        children_age_ranges: data.children_age_ranges || [],
      });
    }
  }, [personaDataResult, form]);

  const resetFormToDefaults = useCallback(() => {
    setDynamicBehaviors([]);
    setDynamicInterests([]);
    setDynamicPersonality([]);

    form.reset({
      age: undefined,
      gender: undefined,
      marital_status: undefined,
      education: undefined,
      rentorhomeowner: undefined,
      behaviors: [],
      interests: [],
      personality: [],
      children_between_ages_0_3: undefined,
      children_age_ranges: [],
    });
  }, [form]);

  const handleCreateBehavior = useCallback(
    (value: string) => {
      setDynamicBehaviors((prev) => {
        if (prev.includes(value)) return prev;
        const newOptions = [...prev, value];
        const currentValues = form.getValues('behaviors') || [];
        form.setValue('behaviors', [...currentValues, value]);
        return newOptions;
      });
    },
    [form]
  );

  const handleCreateInterest = useCallback(
    (value: string) => {
      setDynamicInterests((prev) => {
        if (prev.includes(value)) return prev;
        const newOptions = [...prev, value];
        const currentValues = form.getValues('interests') || [];
        form.setValue('interests', [...currentValues, value]);
        return newOptions;
      });
    },
    [form]
  );

  const handleCreatePersonality = useCallback(
    (value: string) => {
      setDynamicPersonality((prev) => {
        if (prev.includes(value)) return prev;
        const newOptions = [...prev, value];
        const currentValues = form.getValues('personality') || [];
        form.setValue('personality', [...currentValues, value]);
        return newOptions;
      });
    },
    [form]
  );

  const handleSubmit = async () => {
    const isValid = await form.trigger();
    if (!isValid) return;

    const formData = form.getValues();

    const payload = {
      ...formData,
      behaviors: arrayToCommaSeparatedString(
        formData.behaviors
      ) as unknown as string[],
      interests: arrayToCommaSeparatedString(
        formData.interests
      ) as unknown as string[],
      personality: arrayToCommaSeparatedString(
        formData.personality
      ) as unknown as string[],
    };

    if (personaDataResult?.data?.persona_data) {
      updatePersonaData(candidateId, payload);
    } else {
      createPersonaData({
        ...payload,
        user_id: candidateId,
        zor_id: organization?.id || '',
      });
    }
  };

  useEffect(() => {
    resetFormToDefaults();
  }, [candidateId, resetFormToDefaults]);

  useEffect(() => {
    if (isPersonaDataSuccess && personaDataResult?.data?.persona_data) {
      resetForm();
    } else if (isPersonaDataSuccess && !personaDataResult?.data?.persona_data) {
      resetFormToDefaults();
    }
  }, [personaDataResult, isPersonaDataSuccess, resetForm, resetFormToDefaults]);

  useEffect(() => {
    if (isUpdatePersonaDataError || isCreatePersonaDataError) {
      toast({
        title: 'Something went wrong',
        description: 'Failed to save persona data. Please try again.',
        variant: 'destructive',
      });
    }
  }, [isUpdatePersonaDataError, isCreatePersonaDataError]);

  useEffect(() => {
    if (isCreatePersonaDataSuccess || isUpdatePersonaDataSuccess) {
      toast({
        title: 'Success',
        description: 'Persona data saved successfully',
      });
    }
  }, [isCreatePersonaDataSuccess, isUpdatePersonaDataSuccess]);

  if (isPersonaDataLoading) {
    return (
      <div className="flex h-full w-full flex-1 items-center justify-center">
        <Loader2 className="size-4 animate-spin" />
      </div>
    );
  }

  return (
    <div className="relative space-y-1.5">
      {(isCreatePersonaDataLoading || isUpdatePersonaDataLoading) && (
        <div className="absolute inset-0 z-50 flex items-center justify-center bg-gray-100/50 backdrop-blur-sm">
          <Loader2 className="size-4 animate-spin" />
        </div>
      )}

      <ZeePersonaDataForm
        form={form}
        dynamicBehaviors={dynamicBehaviors}
        dynamicInterests={dynamicInterests}
        dynamicPersonality={dynamicPersonality}
        onCreateBehavior={handleCreateBehavior}
        onCreateInterest={handleCreateInterest}
        onCreatePersonality={handleCreatePersonality}
      />

      <div className="flex w-full justify-end">
        <Button
          size="sm"
          className="w-full md:w-fit"
          onClick={handleSubmit}
          disabled={isCreatePersonaDataLoading || isUpdatePersonaDataLoading}
        >
          {personaDataResult?.data?.persona_data
            ? 'Update Persona Data'
            : 'Create Persona Data'}
        </Button>
      </div>
    </div>
  );
};

export default LeadPersonaTab;
